import { lazy, Suspense, type ComponentType } from 'react'
import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom'
import { AppShell } from './AppShell'
import { RequireRole } from './components/RequireRole'
import { ConsoleLayout } from './components/ConsoleLayout/ConsoleLayout'

// Rutas de dispositivo (scaffolding) — no se tocan en Feature 001.
const ScreensRoute = lazy(() => import('./routes/screens/ScreensRoute'))
const InvitadoRoute = lazy(() => import('./routes/invitado/InvitadoRoute'))
// F007 §5.3: PWA del invitado modo B — link público sin token.
const GuestRouteB = lazy(() => import('./routes/invitado/GuestRouteB'))
const GuestRouteSlug = lazy(() => import('./routes/invitado/GuestRouteSlug'))
const PhotoboothRoute = lazy(() => import('./routes/photobooth/PhotoboothRoute'))
// PWA pública del DJ — link con token, cola de pedidos en vivo. Sin auth.
const DjConsoleRoute = lazy(() => import('./routes/dj/DjConsoleRoute'))
// Preview descartable de la reforma visual del invitado (one-page híbrido).
const PreviewInvitacionRoute = lazy(
  () => import('./routes/preview/PreviewInvitacionRoute')
)
// Preview embebible que carga el iframe del panel de diseño de la consola.
const PreviewEmbedRoute = lazy(() => import('./routes/preview/PreviewEmbedRoute'))

// F012: rutas públicas (sin auth) de galerías.
// F019 §5.4: el gate consolidado renderiza la PWA del invitado modo B.
// La sub-ruta `/galeria` se eliminó — la galería ahora es tab dentro de
// la PWA del invitado.
const PublicGalleryGateRoute = lazy(
  () => import('./routes/public/galerias/PublicGalleryGateRoute')
)
const PublicSlideshowRoute = lazy(
  () => import('./routes/public/galerias/SlideshowRoute')
)
// F025 §5.2: ranking proyectable de la galería pública. Análogo a
// SlideshowRoute — sin auth, fullscreen, theme del evento.
const PublicRankingRoute = lazy(
  () => import('./routes/public/galerias/PublicRankingRoute')
)

// Rutas de consola (Feature 001).
const LoginRoute = lazy(() => import('./routes/login/LoginRoute'))
const ClientsListRoute = lazy(() => import('./routes/admin/ClientsListRoute'))
const ClientNewRoute = lazy(() => import('./routes/admin/ClientNewRoute'))
// Detalle de cliente (B6 reform consolas, 2026-05-08): el monolito
// ClientDetailRoute (479L) se split en shell + 4 sub-tabs.
const ClientDetailShell = lazy(() => import('./routes/client/ClientDetailShell'))
const ClientResumenTab = lazy(() => import('./routes/client/ClientResumenTab'))
const ClientUsuariosTab = lazy(() => import('./routes/client/ClientUsuariosTab'))
const ClientEventosTab = lazy(() => import('./routes/client/ClientEventosTab'))
const ClientOperadoresTab = lazy(() => import('./routes/client/ClientOperadoresTab'))
const AdminEventsListRoute = lazy(() => import('./routes/admin/EventsListRoute'))
// Operadores (B4 reform consolas, 2026-05-08): lista + form unificados
// admin/cliente. Detectan rol del JWT y ajustan UI condicionalmente.
const OperatorsListShared = lazy(() => import('./routes/operadores/OperatorsListShared'))
const OperatorFormShared = lazy(() => import('./routes/operadores/OperatorFormShared'))
const ClienteEventsListRoute = lazy(() => import('./routes/cliente/EventsListRoute'))
// Editor de evento (B2 reform consolas, 2026-05-08): el monolito
// EventFormRoute (533L) se split en shell + 4 tabs sub-ruteadas. Mismos
// componentes para cliente y admin — el shell detecta rol del JWT.
const EventNewRoute = lazy(() => import('./routes/event/EventNewRoute'))
const EventEditShell = lazy(() => import('./routes/event/EventEditShell'))
const EventGeneralTab = lazy(() => import('./routes/event/EventGeneralTab'))
const EventBrandingTab = lazy(() => import('./routes/event/EventBrandingTab'))
const EventOperadoresTab = lazy(() => import('./routes/event/EventOperadoresTab'))
const EventLinksTab = lazy(() => import('./routes/event/EventLinksTab'))
const EventGaleriaTab = lazy(() => import('./routes/event/EventGaleriaTab'))
// F029 C: tab Experiencias — setea `module_available_from` por experiencia.
const EventExperienciasTab = lazy(() => import('./routes/event/EventExperienciasTab'))
// F022: ABM de pantallas (devices type='totem') pre-armadas desde la consola.
const EventPantallasTab = lazy(() => import('./routes/event/EventPantallasTab'))
// F025: tab Ranking con polling 10s + top 10 por juego activo.
const EventRankingTab = lazy(() => import('./routes/event/EventRankingTab'))
// Consolidación 9→5 tabs: secciones agrupadas que apilan componentes
// existentes. EventInvitadosSection apila EventInvitadosTab (F028, vista
// unificada personas + invitaciones + dieta) + EventLinksTab. Las rutas
// viejas (pantallas/galeria/ranking/operadores/links) se mantienen montadas
// para back-compat.
const EventInvitadosSection = lazy(() => import('./routes/event/EventInvitadosSection'))
const EventDispositivosTab = lazy(() => import('./routes/event/EventDispositivosTab'))
// Cliente operadores: usa los shared de B4 (no más versiones cliente
// específicas).

// Rutas de consola del operador (Feature 004).
const OperadorEventsListRoute = lazy(() => import('./routes/operador/EventsListRoute'))
const OperadorEventDashboardRoute = lazy(() => import('./routes/operador/EventDashboardRoute'))

// Homes de cada rol (B3 reform consolas, 2026-05-08). Reemplazan los
// `<Navigate>` index a los listados — al loguearse el usuario cae en
// un dashboard con stats + accesos rápidos.
const AdminHome = lazy(() => import('./routes/home/AdminHome'))
const ClienteHome = lazy(() => import('./routes/home/ClienteHome'))
const OperadorHome = lazy(() => import('./routes/home/OperadorHome'))

// Sub-rutas de módulos (Feature 006+). Cada módulo configurable vive en
// /<admin|cliente>/eventos/:id/modulos/<slug>.
// F028: `/modulos/reception` eliminada — la búsqueda + edición de
// personas + mesa quedan cubiertas por `/invitados`.
const MemoryConfigEditor = lazy(
  () => import('./routes/modulos/games.memory/MemoryConfigEditor')
)
const TriviaConfigEditor = lazy(
  () => import('./routes/modulos/games.trivia/TriviaConfigEditor')
)
const WheelConfigEditor = lazy(
  () => import('./routes/modulos/games.wheel/WheelConfigEditor')
)
const PuzzleConfigEditor = lazy(
  () => import('./routes/modulos/games.puzzle/PuzzleConfigEditor')
)
const SessionConfigEditor = lazy(
  () => import('./routes/modulos/games.session/SessionConfigEditor')
)
const PhotoboothConfigEditor = lazy(
  () => import('./routes/modulos/photobooth/PhotoboothConfigEditor')
)
const DjConfigEditor = lazy(
  () => import('./routes/modulos/dj/DjConfigEditor')
)
const GalleryConfigEditor = lazy(
  () => import('./routes/modulos/gallery/GalleryConfigEditor')
)
// F029 — config de los módulos de info del one-page del invitado.
const InvitationLocationConfig = lazy(
  () => import('./routes/modulos/invitation/LocationConfigPage')
)
const InvitationDresscodeConfig = lazy(
  () => import('./routes/modulos/invitation/DresscodeConfigPage')
)
const InvitationGiftConfig = lazy(
  () => import('./routes/modulos/invitation/GiftConfigPage')
)
const InvitationGalleryConfig = lazy(
  () => import('./routes/modulos/invitation/GalleryConfigPage')
)
const InvitationStoryConfig = lazy(
  () => import('./routes/modulos/invitation/StoryConfigPage')
)
// F033 — música de fondo de la invitación.
const InvitationMusicConfig = lazy(
  () => import('./routes/modulos/invitation/MusicConfigPage')
)
// F028 Fase 4b: InvitationsConfigPage eliminado. La funcionalidad se
// redistribuyó: settings card + CRUD modal en EventInvitadosTab,
// send batch en su propia vista, messages/check-ins como rutas
// dedicadas bajo /invitados/*.
const EventInvitadosEnviarRoute = lazy(
  () => import('./routes/event/EventInvitadosEnviarRoute')
)
const EventCheckInsRoute = lazy(
  () => import('./routes/event/EventCheckInsRoute')
)
const EventMessagesRoute = lazy(
  () => import('./routes/event/EventMessagesRoute')
)
const ClienteGaleriasRoute = lazy(
  () => import('./routes/cliente/GaleriasRoute')
)
const AdminGaleriasRoute = lazy(
  () => import('./routes/admin/GaleriasRoute')
)

function RouteLoader() {
  return <div style={{ padding: '2rem', color: 'var(--color-text-muted)' }}>Cargando…</div>
}

function lazyElement(Component: ComponentType) {
  return (
    <Suspense fallback={<RouteLoader />}>
      <Component />
    </Suspense>
  )
}

/**
 * Redirect de back-compat: la consola se movió de `/login|/admin|/cliente|
 * /operador` a `/app/*` (2026-06-23) para que la PWA de consola tenga
 * `scope: /app` y deje de capturar las URLs de invitado (`/invitado/*`,
 * `/galerias/*`). Las URLs viejas (bookmarks, PWA de consola ya instalada,
 * links compartidos) caen acá y se reescriben anteponiendo `/app`,
 * preservando el resto del path + el querystring.
 */
function LegacyConsoleRedirect() {
  const loc = useLocation()
  return <Navigate to={`/app${loc.pathname}${loc.search}`} replace />
}

export const router = createBrowserRouter([
  {
    element: <AppShell />,
    children: [
      // F020: index redirect a `/login` — coherente con el manifest base
      // de Tivy (`start_url: '/login'`, `display: 'standalone'`). Pre-F020
      // redirigía a `/screens` y el manifest era fullscreen, lo cual hacía
      // que cualquier instalación de la PWA se comportara como tótem sin
      // querer. El usuario que abre `/` en browser normal va a login;
      // las PWAs Tivy Display y Tivy Photobooth tienen sus propios
      // `start_url` y arrancan directo en `/screens` o `/photobooth`.
      { index: true, element: <Navigate to="/app/login" replace /> },
      { path: '/screens', element: lazyElement(ScreensRoute) },
      // F007 §5.4: la ruta más específica `/invitado/e/:event_id` (modo B)
      // va declarada **antes** que `/invitado/:token` para que React Router
      // v7 no la resuelva al token "e".
      { path: '/invitado/e/:event_id', element: lazyElement(GuestRouteB) },
      // Link legible del invitado anónimo. Prefijo propio para que un slug
      // nunca pueda chocar con una ruta del producto.
      { path: '/i/:slug', element: lazyElement(GuestRouteSlug) },
      { path: '/invitado/:token', element: lazyElement(InvitadoRoute) },
      { path: '/photobooth', element: lazyElement(PhotoboothRoute) },
      // PWA pública del DJ (sin auth). El token resuelve el evento; la
      // consola muestra la cola de pedidos en vivo.
      { path: '/dj/:token', element: lazyElement(DjConsoleRoute) },
      { path: '/preview/invitacion', element: lazyElement(PreviewInvitacionRoute) },
      { path: '/preview/embed', element: lazyElement(PreviewEmbedRoute) },
      // F029 — Catálogo público de templates (link shareable para clientes).
      { path: '/templates', element: lazyElement(PreviewInvitacionRoute) },
      { path: '/template', element: <Navigate to="/templates" replace /> },

      // Galerías públicas (sin auth). La sub-ruta `/galeria` se eliminó
      // en F019 (consolidación 2026-05-09) — la vista de galería pasó a
      // ser tab dentro de la PWA del invitado modo B. El gate consolidado
      // renderiza directamente `<AnonymousGuestPWA>` con todos los
      // módulos del evento activo. Slideshow es display LED externo y
      // queda intacto.
      {
        path: '/galerias/:client_id/:token/slideshow',
        element: lazyElement(PublicSlideshowRoute)
      },
      // F025 §5.2: ranking proyectable. Path param `public_id` (vs
      // `token` legacy) — el handler backend resuelve por `devices`
      // post-F022.
      {
        path: '/galerias/:client_id/:public_id/ranking',
        element: lazyElement(PublicRankingRoute)
      },
      {
        path: '/galerias/:client_id/:token',
        element: lazyElement(PublicGalleryGateRoute)
      },

      // Login.
      { path: '/app/login', element: lazyElement(LoginRoute) },

      // Consola super admin.
      {
        path: '/app/admin',
        element: <RequireRole role="super_admin" />,
        children: [
          {
            element: <ConsoleLayout />,
            children: [
              { index: true, element: lazyElement(AdminHome) },
              { path: 'clientes', element: lazyElement(ClientsListRoute) },
              { path: 'clientes/nuevo', element: lazyElement(ClientNewRoute) },
              {
                path: 'clientes/:id',
                element: lazyElement(ClientDetailShell),
                children: [
                  { index: true, element: lazyElement(ClientResumenTab) },
                  { path: 'usuarios', element: lazyElement(ClientUsuariosTab) },
                  { path: 'eventos', element: lazyElement(ClientEventosTab) },
                  { path: 'operadores', element: lazyElement(ClientOperadoresTab) }
                ]
              },
              { path: 'eventos', element: lazyElement(AdminEventsListRoute) },
              { path: 'eventos/nuevo', element: lazyElement(EventNewRoute) },
              {
                path: 'eventos/:id',
                element: lazyElement(EventEditShell),
                children: [
                  { index: true, element: lazyElement(EventGeneralTab) },
                  { path: 'branding', element: lazyElement(EventBrandingTab) },
                  { path: 'pantallas', element: lazyElement(EventPantallasTab) },
                  { path: 'galeria', element: lazyElement(EventGaleriaTab) },
                  { path: 'experiencias', element: lazyElement(EventExperienciasTab) },
                  { path: 'ranking', element: lazyElement(EventRankingTab) },
                  { path: 'invitados', element: lazyElement(EventInvitadosSection) },
                  { path: 'dispositivos', element: lazyElement(EventDispositivosTab) },
                  { path: 'operadores', element: lazyElement(EventOperadoresTab) },
                  { path: 'links', element: lazyElement(EventLinksTab) }
                ]
              },
              // F028 Fase 4b: sub-rutas dedicadas bajo /invitados.
              // `/invitados/invitaciones` ELIMINADO — el CRUD de
              // invitations vive ahora como modal "Gestionar invitación"
              // desde EventInvitadosTab (ADR 010: vista canónica única).
              {
                path: 'eventos/:id/invitados/enviar',
                element: lazyElement(EventInvitadosEnviarRoute)
              },
              {
                path: 'eventos/:id/invitados/check-ins',
                element: lazyElement(EventCheckInsRoute)
              },
              {
                path: 'eventos/:id/invitados/mensajes',
                element: lazyElement(EventMessagesRoute)
              },
              // F028 redirects de paths viejos → nuevos. Mantenemos
              // backward-compat para usuarios con bookmarks de la consola.
              {
                path: 'eventos/:id/modulos/invitations',
                element: <Navigate to="../../invitados" relative="path" replace />
              },
              {
                path: 'eventos/:id/modulos/invitations/check-ins',
                element: <Navigate to="../../invitados/check-ins" relative="path" replace />
              },
              {
                path: 'eventos/:id/modulos/invitations/messages',
                element: <Navigate to="../../invitados/mensajes" relative="path" replace />
              },
              {
                path: 'eventos/:id/modulos/reception',
                element: <Navigate to="../../invitados" relative="path" replace />
              },
              {
                path: 'eventos/:id/modulos/games.memory',
                element: lazyElement(MemoryConfigEditor)
              },
              {
                path: 'eventos/:id/modulos/games.trivia',
                element: lazyElement(TriviaConfigEditor)
              },
              {
                path: 'eventos/:id/modulos/games.wheel',
                element: lazyElement(WheelConfigEditor)
              },
              {
                path: 'eventos/:id/modulos/games.puzzle',
                element: lazyElement(PuzzleConfigEditor)
              },
              {
                path: 'eventos/:id/modulos/games.session',
                element: lazyElement(SessionConfigEditor)
              },
              {
                path: 'eventos/:id/modulos/photobooth',
                element: lazyElement(PhotoboothConfigEditor)
              },
              {
                path: 'eventos/:id/modulos/gallery',
                element: lazyElement(GalleryConfigEditor)
              },
              {
                path: 'eventos/:id/modulos/dj',
                element: lazyElement(DjConfigEditor)
              },
              {
                path: 'eventos/:id/modulos/invitation.location',
                element: lazyElement(InvitationLocationConfig)
              },
              {
                path: 'eventos/:id/modulos/invitation.dresscode',
                element: lazyElement(InvitationDresscodeConfig)
              },
              {
                path: 'eventos/:id/modulos/invitation.gift',
                element: lazyElement(InvitationGiftConfig)
              },
              {
                path: 'eventos/:id/modulos/invitation.gallery',
                element: lazyElement(InvitationGalleryConfig)
              },
              {
                path: 'eventos/:id/modulos/invitation.story',
                element: lazyElement(InvitationStoryConfig)
              },
              {
                path: 'eventos/:id/modulos/invitation.music',
                element: lazyElement(InvitationMusicConfig)
              },
              { path: 'operadores', element: lazyElement(OperatorsListShared) },
              { path: 'operadores/nuevo', element: lazyElement(OperatorFormShared) },
              { path: 'operadores/:id', element: lazyElement(OperatorFormShared) },
              { path: 'galerias', element: lazyElement(AdminGaleriasRoute) }
            ]
          }
        ]
      },

      // Consola cliente.
      {
        path: '/app/cliente',
        element: <RequireRole role="client" />,
        children: [
          {
            element: <ConsoleLayout />,
            children: [
              { index: true, element: lazyElement(ClienteHome) },
              { path: 'eventos', element: lazyElement(ClienteEventsListRoute) },
              { path: 'eventos/nuevo', element: lazyElement(EventNewRoute) },
              {
                path: 'eventos/:id',
                element: lazyElement(EventEditShell),
                children: [
                  { index: true, element: lazyElement(EventGeneralTab) },
                  { path: 'branding', element: lazyElement(EventBrandingTab) },
                  { path: 'pantallas', element: lazyElement(EventPantallasTab) },
                  { path: 'galeria', element: lazyElement(EventGaleriaTab) },
                  { path: 'experiencias', element: lazyElement(EventExperienciasTab) },
                  { path: 'ranking', element: lazyElement(EventRankingTab) },
                  { path: 'invitados', element: lazyElement(EventInvitadosSection) },
                  { path: 'dispositivos', element: lazyElement(EventDispositivosTab) },
                  { path: 'operadores', element: lazyElement(EventOperadoresTab) },
                  { path: 'links', element: lazyElement(EventLinksTab) }
                ]
              },
              // F028: idem admin — paths legacy se redirigen a /invitados.
              {
                path: 'eventos/:id/invitados/enviar',
                element: lazyElement(EventInvitadosEnviarRoute)
              },
              {
                path: 'eventos/:id/invitados/check-ins',
                element: lazyElement(EventCheckInsRoute)
              },
              {
                path: 'eventos/:id/invitados/mensajes',
                element: lazyElement(EventMessagesRoute)
              },
              {
                path: 'eventos/:id/modulos/invitations',
                element: <Navigate to="../../invitados" relative="path" replace />
              },
              {
                path: 'eventos/:id/modulos/invitations/check-ins',
                element: <Navigate to="../../invitados/check-ins" relative="path" replace />
              },
              {
                path: 'eventos/:id/modulos/invitations/messages',
                element: <Navigate to="../../invitados/mensajes" relative="path" replace />
              },
              {
                path: 'eventos/:id/modulos/reception',
                element: <Navigate to="../../invitados" relative="path" replace />
              },
              {
                path: 'eventos/:id/modulos/games.memory',
                element: lazyElement(MemoryConfigEditor)
              },
              {
                path: 'eventos/:id/modulos/games.trivia',
                element: lazyElement(TriviaConfigEditor)
              },
              {
                path: 'eventos/:id/modulos/games.wheel',
                element: lazyElement(WheelConfigEditor)
              },
              {
                path: 'eventos/:id/modulos/games.puzzle',
                element: lazyElement(PuzzleConfigEditor)
              },
              {
                path: 'eventos/:id/modulos/games.session',
                element: lazyElement(SessionConfigEditor)
              },
              {
                path: 'eventos/:id/modulos/photobooth',
                element: lazyElement(PhotoboothConfigEditor)
              },
              {
                path: 'eventos/:id/modulos/gallery',
                element: lazyElement(GalleryConfigEditor)
              },
              {
                path: 'eventos/:id/modulos/dj',
                element: lazyElement(DjConfigEditor)
              },
              {
                path: 'eventos/:id/modulos/invitation.location',
                element: lazyElement(InvitationLocationConfig)
              },
              {
                path: 'eventos/:id/modulos/invitation.dresscode',
                element: lazyElement(InvitationDresscodeConfig)
              },
              {
                path: 'eventos/:id/modulos/invitation.gift',
                element: lazyElement(InvitationGiftConfig)
              },
              {
                path: 'eventos/:id/modulos/invitation.gallery',
                element: lazyElement(InvitationGalleryConfig)
              },
              {
                path: 'eventos/:id/modulos/invitation.story',
                element: lazyElement(InvitationStoryConfig)
              },
              {
                path: 'eventos/:id/modulos/invitation.music',
                element: lazyElement(InvitationMusicConfig)
              },
              { path: 'operadores', element: lazyElement(OperatorsListShared) },
              { path: 'operadores/nuevo', element: lazyElement(OperatorFormShared) },
              { path: 'operadores/:id', element: lazyElement(OperatorFormShared) },
              { path: 'galerias', element: lazyElement(ClienteGaleriasRoute) }
            ]
          }
        ]
      },

      // Consola operador (Feature 004).
      {
        path: '/app/operador',
        element: <RequireRole role="operator" />,
        children: [
          {
            element: <ConsoleLayout />,
            children: [
              { index: true, element: lazyElement(OperadorHome) },
              { path: 'eventos', element: lazyElement(OperadorEventsListRoute) },
              { path: 'evento/:id', element: lazyElement(OperadorEventDashboardRoute) }
            ]
          }
        ]
      },

      // Fallback. F020: coherente con el index redirect — URLs inválidas
      // van a /login (entrada canónica de la consola), no a /screens
      // (que ahora es la superficie de Tivy Display, no un fallback
      // genérico).

      // Back-compat: rutas viejas de la consola (pre-/app) → `/app/...`,
      // preservando path + querystring. Cubre bookmarks, la PWA de consola
      // ya instalada (start_url viejo /login) y links compartidos.
      { path: '/login', element: <LegacyConsoleRedirect /> },
      { path: '/admin/*', element: <LegacyConsoleRedirect /> },
      { path: '/cliente/*', element: <LegacyConsoleRedirect /> },
      { path: '/operador/*', element: <LegacyConsoleRedirect /> },

      { path: '*', element: <Navigate to="/app/login" replace /> }
    ]
  }
])
